interface Entity {
  entityId: string
  slot: string
  typeId: string
  description: string
  entId: number
  category: number
  trackingEnabled: boolean
}
export type GroupOptions = {
  newSlot?: string
  description?: string
  valueJoiner?: string // default ' '
  idJoiner?: string // default ''
  computeValue?: (items: SlotData[]) => string
}
export interface SlotData {
  description: string
  entId: number
  slot: string
  typeId: string
  value: string
  entityId: string
}

interface Data {
  importedJson?: Record<string, string>
  ecsExtractions?: Record<string, string>
  ascExtractions?: Record<string, string>
  extraction?: {
    extractionJson?: Record<string, string>
  }
  [key: string]: any
}

interface MappedEntity {
  slot: string
  typeId: string
  description: string
  entId: number
  value: string
  entityId: string
}

type MapOptions = { slot: string; onlyValue?: false } | { slot: string; onlyValue: true }

export function mapEntities(entities: Entity[], data: Data | Data[]): MappedEntity[]
export function mapEntities(
  entities: Entity[],
  data: Data | Data[],
  opts: { slot: string; onlyValue?: false },
): MappedEntity | undefined
export function mapEntities(
  entities: Entity[],
  data: Data | Data[],
  opts: { slot: string; onlyValue: true },
): string | undefined

export function mapEntities(
  entities: Entity[],
  data: Data | Data[],
  opts?: MapOptions,
): MappedEntity[] | MappedEntity | string | undefined {
  const src: Data = Array.isArray(data) ? data[0] ?? {} : data
  const ecsExtractions = src.ecsExtractions ?? {}
  const importedJson = src.importedJson ?? {}
  const ascExtractions = src.ascExtractions ?? {}
  const extractionJson = src.extraction?.extractionJson ?? {}

  const getValueForEntId = (entId: number): string => {
    const key = String(entId)
    if (Object.prototype.hasOwnProperty.call(ecsExtractions, key)) {
      return ecsExtractions[key] ?? ''
    }
    if (Object.prototype.hasOwnProperty.call(importedJson, key)) {
      return importedJson[key] ?? ''
    }
    if (Object.prototype.hasOwnProperty.call(extractionJson, key)) {
      return extractionJson[key] ?? ''
    }
    if (Object.prototype.hasOwnProperty.call(ascExtractions, key)) {
      return ascExtractions[key] ?? ''
    }
    return ''
  }

  if (opts && 'slot' in opts) {
    const e = entities.find((en) => en.slot === opts.slot)
    if (!e) return opts.onlyValue ? undefined : undefined

    const value = getValueForEntId(e.entId)

    if (opts.onlyValue) {
      return value || undefined
    }

    return {
      slot: e.slot,
      typeId: e.typeId,
      description: e.description,
      entId: e.entId,
      entityId: e.entityId,
      value: value ? value : '---',
    }
  }

  return entities.map((e) => ({
    slot: e.slot,
    typeId: e.typeId,
    description: e.description,
    entId: e.entId,
    entityId: e.entityId,
    value: getValueForEntId(e.entId)?.toString() || '---',
  }))
}

export function filterSlotObjects<T extends { slot: string }>(slotData: T[], slotGroup: string[]): T[] {
  const slotSet = new Set(slotGroup)
  return slotData.filter((s) => slotSet.has(s.slot))
}

function toNumber(n: string | number | undefined): number {
  const x = Number(n)
  return Number.isFinite(x) ? x : 0
}

const joinNonEmpty = (arr: (string | number | undefined)[], sep: string) =>
  arr
    .map((x) => (x == null ? '' : String(x).trim()))
    .filter(Boolean)
    .join(sep)

export function groupSlotsandOverrideDescription(
  slotData: SlotData[],
  slotsToGroup: string[],
  opts: GroupOptions = {},
): SlotData[] {
  if (!Array.isArray(slotData) || !Array.isArray(slotsToGroup) || slotsToGroup.length === 0) return slotData

  const bySlot = new Map(slotData.map((s) => [s.slot, s] as const))
  const allPresent = slotsToGroup.every((s) => bySlot.has(s))
  if (!allPresent) return slotData

  const indices = slotsToGroup
    .map((s) => slotData.findIndex((x) => x.slot === s))
    .filter((i) => i >= 0)
    .sort((a, b) => a - b)
  if (indices.length === 0) return slotData
  const insertIndex = indices[0]

  const items = slotsToGroup.map((s) => bySlot.get(s)!)

  const { newSlot, description, valueJoiner = ' ', idJoiner = '', computeValue } = opts

  const groupedValue = computeValue
    ? computeValue(items)
    : joinNonEmpty(
      items.map((i) => i.value),
      valueJoiner,
    )

  const grouped: SlotData = {
    slot: newSlot ?? slotsToGroup.join('+'),
    description: description ?? (items[0]?.description || 'Grouped Value'),
    value: groupedValue,
    entId: toNumber(
      joinNonEmpty(
        items.map((i) => i.entId),
        idJoiner,
      ),
    ),
    typeId: joinNonEmpty(
      items.map((i) => i.typeId),
      idJoiner,
    ),
    entityId: joinNonEmpty(
      items.map((i) => i.entityId),
      idJoiner,
    ),
  }

  const remove = new Set(slotsToGroup)
  const remaining = slotData.filter((s) => !remove.has(s.slot))
  return [...remaining.slice(0, insertIndex), grouped, ...remaining.slice(insertIndex)]
}

export type ValueFromLines = Array<{ of: string[]; joiner?: string }>

export type GroupRule = {
  slots: string[]
  options?: GroupOptions
  valueFromLines?: ValueFromLines
}

export function groupMany(slotData: SlotData[], rules: GroupRule[]): SlotData[] {
  return rules.reduce((acc, rule) => {
    const { slots, options, valueFromLines } = rule

    let opts = options ?? {}
    if (valueFromLines && !opts.computeValue) {
      opts = {
        ...opts,
        computeValue: (items: SlotData[]) => {
          const valueBySlot = new Map(items.map((i) => [i.slot, i.value] as const))
          const lines = valueFromLines
            .map((line) => {
              const segs = line.of.map((s) => (valueBySlot.get(s) ?? '').trim()).filter(Boolean)
              return segs.join(line.joiner ?? ' ')
            })
            .filter(Boolean)
          return lines.join('\n')
        },
      }
    }

    return groupSlotsandOverrideDescription(acc, slots, opts)
  }, slotData)
}

export type AppealEnumDescriptions = Partial<
  Record<'appeal_status' | 'appeal_submission_method' | 'level_of_appeal', Record<string, string>>
>

export const formatAppealStatusDescription = (description: string): string =>
  description.replace(/^\s*\d+\s*-\s*/, '')

export const mapFilteredSlotObjects = (
  slotSource: SlotData[],
  slots: (string | string[])[],
  appealEnumDescriptions: AppealEnumDescriptions = {},
): SlotData[] => {
  return slots.reduce((result: SlotData[], item) => {
    const slotNames = typeof item === 'string' ? [item] : item
    const firstSlot = slotSource.find((el) => el.slot === slotNames[0])
    if (!firstSlot) return result

    const selected =
      slotNames
        .map((name) => slotSource.find((el) => el.slot === name))
        .find((el) => el?.value && el.value !== '---') || firstSlot

    const enumDescription = appealEnumDescriptions[selected.slot as keyof AppealEnumDescriptions]?.[selected.value]

    result.push({
      slot: selected.slot,
      description: firstSlot.description,
      entId: selected.entId,
      typeId: selected.typeId,
      value:
        selected.slot === 'appeal_status' && enumDescription
          ? formatAppealStatusDescription(enumDescription)
          : enumDescription ?? selected.value,
      entityId: selected.entityId,
    })

    return result
  }, [])
}
